import { LockOutlined, LoginOutlined, MailOutlined, TruckOutlined } from "@ant-design/icons";
import { Alert, App, Button, Checkbox, Form, Input } from "antd";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getCurrentUserProfileApi, loginApi } from "../../api/authApi";
import { getDefaultActiveRole } from "../../features/auth/roleContext";
import { useAuthStore } from "../../stores/authStore";
import { getApiErrorMessage } from "../../utils/apiError";

type LoginFormValues = {
  email: string;
  password: string;
  remember?: boolean;
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const setSession = useAuthStore((x) => x.setSession);
  const setUser = useAuthStore((x) => x.setUser);
  const setActiveRole = useAuthStore((x) => x.setActiveRole);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const year = new Date().getFullYear();
  const rememberedEmail = typeof localStorage !== "undefined" ? localStorage.getItem("transport-login-email") ?? "" : "";

  const returnUrlFromState =
    typeof location.state === "object" &&
    location.state !== null &&
    "from" in location.state &&
    typeof (location.state as { from?: unknown }).from === "string"
      ? (location.state as { from: string }).from
      : null;

  const returnUrlFromQuery = new URLSearchParams(location.search).get("returnUrl");
  const returnUrl = [returnUrlFromState, returnUrlFromQuery]
    .find((value): value is string => Boolean(value && value.startsWith("/"))) ?? "/";

  const onFinish = async (values: LoginFormValues) => {
    setLoading(true);
    setErrorText(null);
    try {
      if (values.remember) localStorage.setItem("transport-login-email", values.email);
      else localStorage.removeItem("transport-login-email");
      const result = await loginApi(values.email, values.password);

      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        accessTokenExpiresOnUtc: result.accessTokenExpiresAt,
        refreshTokenExpiresOnUtc: null,
        tenantId: String(result.organizationId ?? 1),
        branchId: "main",
        activeRole: null,
        user: {
          id: result.userId,
          username: result.email,
          displayName: result.email,
          roles: [],
          permissions: []
        }
      });

      try {
        const me = await getCurrentUserProfileApi();
        setUser({
          id: me.userId,
          username: me.username,
          displayName: me.displayName,
          roles: me.roles ?? [],
          permissions: me.permissions ?? []
        });

        // Persist branch assignment returned from server
      if (me.branchId) {
        useAuthStore.setState({
          branchId: String(me.branchId),
          branchName: me.branchName ?? null,
          homeBranchId: String(me.branchId),
          homeBranchName: me.branchName ?? null
        });
      }

        const defaultRole = getDefaultActiveRole(me.roles ?? []);
        if (defaultRole) {
          setActiveRole(defaultRole);
        }
      } catch {
        // Keep session alive even if profile endpoint is temporarily unavailable.
      }

      message.success("Login successful.");
      navigate(returnUrl, { replace: true });
    } catch (error) {
      const parsed = getApiErrorMessage(error, "Unable to login");
      setErrorText(parsed);
      message.error(parsed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <aside className="auth-brand-panel">
        <div className="auth-brand-logo">
          <div className="auth-brand-logo-icon"><TruckOutlined /></div>
          <span className="auth-brand-logo-name">Shingnapur Carrying Corporation</span>
        </div>

        <div className="auth-brand-content">
          <h1 className="auth-brand-headline">Smart Transport Operations<br />for Modern Logistics</h1>
          <p className="auth-brand-sub">
            Complete ERP workspace for consignments, manifests, fleet operations, branch stationery,
            accounting and compliance.
          </p>
          <ul className="auth-brand-features">
            <li>Branch and financial-year controlled operations</li>
            <li>Consignment, manifest and gate-pass workflows</li>
            <li>Manual stationery and automatic document sequences</li>
            <li>Fleet, driver and lorry-hire management</li>
            <li>Role-based access with operational reports</li>
          </ul>
        </div>

        <div className="auth-brand-footer">&copy; {year} Shingnapur Carrying Corporation - All rights reserved</div>
      </aside>

      <main className="auth-form-panel">
        <div className="auth-form-card">
          <div className="auth-mobile-logo">
            <div className="auth-mobile-logo-icon"><TruckOutlined /></div>
            <span className="auth-mobile-logo-name">Shingnapur Carrying Corporation</span>
          </div>

          <div className="auth-card-header">
            <h2 className="auth-card-title">Welcome back</h2>
            <p className="auth-card-subtitle">Sign in to your account to continue</p>
          </div>

          {errorText ? <Alert className="auth-alert" type="error" showIcon message={errorText} /> : null}

          <Form<LoginFormValues>
            layout="vertical"
            initialValues={{ email: rememberedEmail, remember: Boolean(rememberedEmail) }}
            onFinish={onFinish}
          >
            <Form.Item className="auth-field" name="email" label="Email address" rules={[{ required: true, message: "Email is required" }, { type: "email", message: "Enter a valid email address" }]}>
              <Input prefix={<MailOutlined />} placeholder="you@company.com" autoComplete="username" autoFocus />
            </Form.Item>
            <Form.Item className="auth-field" name="password" label="Password" rules={[{ required: true, message: "Password is required" }]}>
              <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" autoComplete="current-password" />
            </Form.Item>
            <div className="auth-options">
              <Form.Item name="remember" valuePropName="checked" noStyle><Checkbox className="auth-remember">Remember me</Checkbox></Form.Item>
            </div>
            <Button className="btn-auth-submit" type="primary" htmlType="submit" icon={<LoginOutlined />} loading={loading} block>Sign In</Button>
          </Form>

          <div className="auth-card-footer">Secure access to your organisation workspace.
          </div>
        </div>
        <div className="auth-page-footer">&copy; {year} Shingnapur Carrying Corporation - All rights reserved</div>
      </main>
    </div>
  );
};
